import React, { useEffect } from 'react';
import { IconButton } from '@material-ui/core';
import styled from 'styled-components';
import { color } from 'styles/utils';
import cx from 'classnames';
import { useDidUpdateEffect } from 'lib/utils';
import { reworkTypsList } from 'lib/mapper';
import MuiButton from 'components/common/button/MuiButton';
import T from 'components/common/text/T';
import { StyledPlainButtonOuter } from 'components/common/styled/Button';
import Color from 'color';
import CheckIcon from '@material-ui/icons/Check';
import useCheckSetInput from 'lib/hooks/useCheckSetInput';
import { useProjectDetailStore } from 'store/local/projectDetail';
import { useProjectDetail } from 'lib/hooks/project/useProjectDetail';
import { useProject } from 'lib/hooks/project/useProject';

interface ProjectReworkModalProps {}

function ProjectReworkModal({}: ProjectReworkModalProps) {
  const { reworkList, setReworkList } = useProjectDetailStore();
  const { handleCloseReworkStatusModal, handleEditRework } = useProjectDetail();

  const { editReworkSuccess } = useProject();
  const reworkCheckValue = useCheckSetInput(new Set([]));
  const handleCheckRework = (index: number) => {
    reworkCheckValue.onChange({ value: index });
  };

  useEffect(() => {
    const returnValue = reworkList.reduce((acc: any[], curr: any) => {
      return acc.concat(curr.reworkTypeIdx);
    }, []);
    reworkCheckValue.setValue(new Set([...returnValue]));
  }, []);

  const handleSaveRework = () => {
    handleEditRework([...reworkCheckValue.value]);
  };

  useDidUpdateEffect(() => {
    if (editReworkSuccess) {
      handleCloseReworkStatusModal();
      const newReworkList = [...reworkCheckValue.value].reduce((acc: any[], curr: any) => {
        const rework = reworkTypsList.find(item => item.reworkTypeIdx === curr);
        const obj = {
          reworkTypeIdx: rework?.reworkTypeIdx,
          reworkTypeName: rework?.reworkTypeName,
          reworkTypeCode: rework?.reworkTypeCode,
        };
        return acc.concat(obj);
      }, []);

      const sortValue = newReworkList.sort(function (a: any, b: any) {
        return a.reworkTypeIdx - b.reworkTypeIdx;
      });
      setReworkList(sortValue);
    }
  }, [editReworkSuccess]);

  return (
    <StyledProjectReworkModal data-component-name="ProjectReworkModal">
      <div className="ProjectReworkModal__container_wrapper">
        <div className="ProjectReworkModal__title_container">
          <h1 className="ProjectReworkModal__title_box">
            <T>REWORK_TITLE</T>
          </h1>
          <div className="ProjectReworkModal__title_underbar"></div>
        </div>
        <div className="ProjectReworkModal__contents_container">
          {reworkTypsList.map((reworkItem: any) => {
            const checked = reworkCheckValue.value.has(reworkItem.reworkTypeIdx);
            return (
              <IconButton
                className={cx('ProjectReworkModal__contents_box', {
                  checked: !!checked,
                })}
                key={reworkItem.reworkTypeIdx}
                onClick={() => handleCheckRework(reworkItem.reworkTypeIdx)}
              >
                <div
                  className={cx('ProjectReworkModal__contents_check_box', {
                    checked: !!checked,
                  })}
                >
                  <CheckIcon htmlColor="inherit" fontSize="inherit" />
                </div>
                <span className="ProjectReworkModal__contents_name">
                  <T>{reworkItem.reworkTypeCode}</T>
                </span>
              </IconButton>
            );
          })}
        </div>
      </div>
      <StyledPlainButtonOuter
        className="partnerModalRequest__button_container"
        backgroundColor={color.navy_blue}
        left="50%"
        width={410}
        height={76}
      >
        <div className="partnerModalRequest__button_box">
          <MuiButton
            config={{
              width: '95px',
              color: color.gray_week,
            }}
            disableElevation
            variant="contained"
            className="xl border-radius-round inset-shadow-default partnerModalRequest__cancel_button"
            onClick={() => {
              handleCloseReworkStatusModal();
            }}
          >
            <T>GLOBAL_CANCEL</T>
          </MuiButton>
          <MuiButton
            config={{
              width: '295px',
            }}
            disableElevation
            variant="contained"
            className="xl border-radius-round inset-shadow-default partnerModalRequest__save_button"
            onClick={() => {
              handleSaveRework();
            }}
          >
            <span className="btn-shadow-inset"></span>
            <T>GLOBAL_SAVE</T>
          </MuiButton>
        </div>
      </StyledPlainButtonOuter>
    </StyledProjectReworkModal>
  );
}

const StyledProjectReworkModal = styled.section<{}>`
  position: relative;
  .ProjectReworkModal__container_wrapper {
    width: 100%;
    height: 460px;
    margin-bottom: 30px;
    position: relative;
    background-color: #ffffff;
    border-radius: 15px;
    padding: 30px 0;

    .ProjectReworkModal__title_container {
      position: relative;
      padding-bottom: 12px;
      text-align: center;
      .ProjectReworkModal__title_box {
        color: #0f0f0f;
        font-size: 22px;
        font-weight: 700;
      }
      .ProjectReworkModal__title_underbar {
        position: absolute;
        width: 80px;
        height: 2px;
        background-color: #1da7e0;
        transform: translate(-50%, -50%);
        left: 50%;
        bottom: 0;
      }
    }

    .ProjectReworkModal__contents_container {
      margin-top: 60px;
      display: flex;
      flex-direction: column;
      row-gap: 10px;
      padding: 0 40px;
      .ProjectReworkModal__contents_box {
        position: relative;
        width: 100%;
        height: 50px;
        border: 1px solid #dddee4;
        border-radius: 5px;
        display: flex;
        align-items: center;
        font-size: 14px;
        color: #444;
        &:hover {
          cursor: pointer;
        }
        .ProjectReworkModal__contents_check_box {
          position: absolute;
          top: 50%;
          transform: translate(0, -50%);
          left: 15px;

          font-size: 12px;
          border: 1px solid #b5b7c1;
          border-radius: 50%;
          color: #444;
          display: flex;
          align-items: center;
          justify-content: center;
          &.checked {
            color: #00a6e2;
            background-color: #f7fcfe;
            border: 1px solid #00a6e2;
          }
        }
        .ProjectReworkModal__contents_name {
          color: inherit;
        }
        &.checked {
          color: #00a6e2;
          background-color: #f7fcfe;
          border: 1px solid #00a6e2;
        }
      }
    }
  }
  .partnerModalRequest__button_container {
    .partnerModalRequest__button_box {
      position: relative;
      display: flex;
      .partnerModalRequest__cancel_button {
        position: relative;
        z-index: 2;
      }
      .partnerModalRequest__save_button {
        margin-left: -10px;
        background: rgb(0, 166, 226);
        background: linear-gradient(90deg, rgba(0, 166, 226, 1) 0%, rgba(8, 123, 238, 1) 100%);
        border: none;
        border-top-left-radius: 0;
        border-bottom-left-radius: 0;
        overflow: hidden;
        &:hover {
          background: ${`linear-gradient(
            90deg,
            ${Color('rgba(0, 166, 226, 1)').darken(0.12)} 0%,
            ${Color('rgba(8, 123, 238, 1)').darken(0.12)} 100%
          )`};
        }
        .btn-shadow-inset {
          position: absolute;
          top: -5px;
          left: -46px;
          width: 66px;
          height: 66px;
          border-radius: 33px;
          background-color: ${color.navy_blue};
          box-shadow: 3px 3px 6px rgba(0, 0, 0, 0.16);
        }
      }
    }
  }
`;

export default React.memo(ProjectReworkModal);
